import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import StandingsWidget from '../../components/stats/StandingsWidget';
import { getRankingCompetitionMeta } from '../../../api';

const parseDayValue = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function StandingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const competitionIdsParam = searchParams.get('competitionIds') || '';
  // const provider = searchParams.get('provider') || '';
  const dayParam = searchParams.get('day');

  const widgetConfigurationId = searchParams.get('widgetConfigurationId') || '';

  const competitionIds = useMemo(
    () =>
      competitionIdsParam
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    [competitionIdsParam]
  );

  const isSingleCompetition = competitionIds.length === 1;
  const firstCompetitionId = competitionIds[0];

  const [discipline, setDiscipline] = useState(null);
  const [title, setTitle] = useState('Classement');
  const [competitionInfos, setCompetitionInfos] = useState([]);
  const [resolvedDay, setResolvedDay] = useState(null);

  useEffect(() => {
    let ignore = false;

    const loadMeta = async () => {
      if (!firstCompetitionId) {
        if (!ignore) {
          setDiscipline(null);
          setResolvedDay(null);
          setTitle('Classement');
          setCompetitionInfos([]);
        }
        return;
      }

      try {
        const metas = await Promise.all(
          competitionIds.map(async (competitionId) => {
            const { data } = await getRankingCompetitionMeta(competitionId);
            return data;
          })
        );

        if (ignore) return;

        const firstMeta = metas[0] || {};
        const metaDiscipline = firstMeta?.discipline || null;
        const metaNbDays = Number(firstMeta?.nbDays);

        setDiscipline(metaDiscipline);
        setTitle(isSingleCompetition ? 'Classement' : 'Classement combiné');
        setCompetitionInfos(
          metas
            .map((meta, index) => ({
              id: competitionIds[index],
              name: meta?.name || '',
              season: meta?.season || '',
            }))
            .filter((item) => item.name)
        );

        const parsedDayFromUrl = parseDayValue(dayParam);

        if (!isSingleCompetition) {
          setResolvedDay(null);

          if (dayParam) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('day');
            setSearchParams(nextParams, { replace: true });
          }

          return;
        }

        if (parsedDayFromUrl != null) {
          setResolvedDay(parsedDayFromUrl);
          return;
        }

        if (Number.isFinite(metaNbDays) && metaNbDays > 0) {
          setResolvedDay(metaNbDays);

          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('day', String(metaNbDays));
          setSearchParams(nextParams, { replace: true });
        } else {
          setResolvedDay(null);
        }
      } catch (error) {
        if (!ignore) {
          setDiscipline(null);
          setResolvedDay(isSingleCompetition ? parseDayValue(dayParam) : null);
          setTitle(isSingleCompetition ? 'Classement' : 'Classement combiné');
          setCompetitionInfos([]);
        }
      }
    };

    loadMeta();

    return () => {
      ignore = true;
    };
  }, [
    firstCompetitionId,
    competitionIds,
    dayParam,
    searchParams,
    setSearchParams,
    isSingleCompetition,
  ]);

  if (!discipline || competitionIds.length === 0) return null;

  return (
    <StandingsWidget
      discipline={discipline}
      competitionIds={competitionIds}
      competitionInfos={competitionInfos}
      day={isSingleCompetition ? resolvedDay : null}
      widgetConfigurationId={widgetConfigurationId || null}
      title={title}
      onNavigate={(url) => navigate(url)}
    />
  );
}